/**
 * Optimized deserialization that creates InternedNodeDto directly.
 * 
 * This file is separate from the auto-generated MemoryPack files to avoid
 * being overwritten. It provides direct deserialization to InternedNodeDto,
 * interning strings during deserialization rather than in a separate pass.
 * 
 * Benefits:
 * - Lower peak memory: strings go directly to StringStorage, no intermediate NodeDto[]
 * - Single pass: deserialize and intern in one step
 * - Same result as deserialize + internNodeArray, but more memory efficient
 * 
 * Usage:
 * ```typescript
 * const storage = createAndSetStorage();
 * const nodes = deserializeConcatenatedToInternedNodes(buffer);
 * // ... use nodes ...
 * cleanupInterning(storage);
 * ```
 */

import { InternedNodeDto } from '@/types/InternedNodeDto';
import { MemoryPackReader } from './MemoryPackReader';
import { ModelParameterDto } from './ModelParameterDto';

/**
 * Deserializes a single node from the reader directly to an InternedNodeDto.
 * String fields are interned during deserialization via InternedNodeDto setters.
 * 
 * @param reader - The MemoryPackReader positioned at the start of a node
 * @returns InternedNodeDto or null if the node header indicates null
 */
function deserializeNodeToInterned(reader: MemoryPackReader): InternedNodeDto | null {
  const [ok, count] = reader.tryReadObjectHeader();
  if (!ok) {
    return null;
  }

  const value = new InternedNodeDto();

  if (count === 43) {
    // Fast path: all 43 properties present
    value.nodeId = reader.readInt64();
    value.nodePath = reader.readString();
    value.nodeType = reader.readInt32();
    value.modelId = reader.readGuid();
    value.cadModelCategoryId = reader.readGuid();
    // Interned string fields - setters handle interning
    value.cadId = reader.readString();
    value.cadFamily = reader.readString();
    value.cadName = reader.readString();
    value.cadMaterial = reader.readString();
    value.cadReferenceId = reader.readString();
    // Non-interned fields
    value.isStructuralMaterial = reader.readNullableBoolean();
    value.scopeId = reader.readNullableInt32();
    value.componentCount = reader.readInt32();
    value.quantity = reader.readNullableFloat64();
    value.length = reader.readNullableFloat64();
    value.area = reader.readNullableFloat64();
    value.volume = reader.readNullableFloat64();
    value.thickness = reader.readNullableFloat64();
    value.originalQuantity = reader.readNullableFloat64();
    value.originalLength = reader.readNullableFloat64();
    value.originalArea = reader.readNullableFloat64();
    value.originalVolume = reader.readNullableFloat64();
    value.originalThickness = reader.readNullableFloat64();
    // More interned string fields
    value.omniClassNumber = reader.readString();
    value.omniClassTitle = reader.readString();
    value.uniFormatNumber = reader.readString();
    value.uniFormatTitle = reader.readString();
    value.masterFormatNumber = reader.readString();
    value.masterFormatTitle = reader.readString();
    value.multiplierInput = reader.readString();
    // Non-interned
    value.multiplierValue = reader.readNullableFloat64();
    // Interned
    value.notes = reader.readString();
    // Non-interned
    value.carbonImpactValue = reader.readNullableFloat64();
    // Interned
    value.carbonImpactInput = reader.readString();
    value.carbonImpactNotes = reader.readString();
    // Non-interned
    value.hasChildCarbonImpactOverride = reader.readBoolean();
    value.systemId = reader.readNullableGuid();
    value.subSystemId = reader.readNullableGuid();
    // Interned
    value.freeText1 = reader.readString();
    value.freeText2 = reader.readString();
    value.freeText3 = reader.readString();
    // Non-interned (co2MaterialTypeSpec is not interned - stored as string directly)
    value.co2MaterialTypeSpec = reader.readString();
    // Non-interned
    value.modelParameters = reader.readArray(r => ModelParameterDto.deserializeCore(r));
  } else if (count > 43) {
    throw new Error("Current object's property count is larger than type schema, can't deserialize about versioning.");
  } else {
    // Slow path: partial properties (versioning support)
    if (count === 0) return value;
    value.nodeId = reader.readInt64(); if (count === 1) return value;
    value.nodePath = reader.readString(); if (count === 2) return value;
    value.nodeType = reader.readInt32(); if (count === 3) return value;
    value.modelId = reader.readGuid(); if (count === 4) return value;
    value.cadModelCategoryId = reader.readGuid(); if (count === 5) return value;
    value.cadId = reader.readString(); if (count === 6) return value;
    value.cadFamily = reader.readString(); if (count === 7) return value;
    value.cadName = reader.readString(); if (count === 8) return value;
    value.cadMaterial = reader.readString(); if (count === 9) return value;
    value.cadReferenceId = reader.readString(); if (count === 10) return value;
    value.isStructuralMaterial = reader.readNullableBoolean(); if (count === 11) return value;
    value.scopeId = reader.readNullableInt32(); if (count === 12) return value;
    value.componentCount = reader.readInt32(); if (count === 13) return value;
    value.quantity = reader.readNullableFloat64(); if (count === 14) return value;
    value.length = reader.readNullableFloat64(); if (count === 15) return value;
    value.area = reader.readNullableFloat64(); if (count === 16) return value;
    value.volume = reader.readNullableFloat64(); if (count === 17) return value;
    value.thickness = reader.readNullableFloat64(); if (count === 18) return value;
    value.originalQuantity = reader.readNullableFloat64(); if (count === 19) return value;
    value.originalLength = reader.readNullableFloat64(); if (count === 20) return value;
    value.originalArea = reader.readNullableFloat64(); if (count === 21) return value;
    value.originalVolume = reader.readNullableFloat64(); if (count === 22) return value;
    value.originalThickness = reader.readNullableFloat64(); if (count === 23) return value;
    value.omniClassNumber = reader.readString(); if (count === 24) return value;
    value.omniClassTitle = reader.readString(); if (count === 25) return value;
    value.uniFormatNumber = reader.readString(); if (count === 26) return value;
    value.uniFormatTitle = reader.readString(); if (count === 27) return value;
    value.masterFormatNumber = reader.readString(); if (count === 28) return value;
    value.masterFormatTitle = reader.readString(); if (count === 29) return value;
    value.multiplierInput = reader.readString(); if (count === 30) return value;
    value.multiplierValue = reader.readNullableFloat64(); if (count === 31) return value;
    value.notes = reader.readString(); if (count === 32) return value;
    value.carbonImpactValue = reader.readNullableFloat64(); if (count === 33) return value;
    value.carbonImpactInput = reader.readString(); if (count === 34) return value;
    value.carbonImpactNotes = reader.readString(); if (count === 35) return value;
    value.hasChildCarbonImpactOverride = reader.readBoolean(); if (count === 36) return value;
    value.systemId = reader.readNullableGuid(); if (count === 37) return value;
    value.subSystemId = reader.readNullableGuid(); if (count === 38) return value;
    value.freeText1 = reader.readString(); if (count === 39) return value;
    value.freeText2 = reader.readString(); if (count === 40) return value;
    value.freeText3 = reader.readString(); if (count === 41) return value;
    value.co2MaterialTypeSpec = reader.readString(); if (count === 42) return value;
    value.modelParameters = reader.readArray(r => ModelParameterDto.deserializeCore(r)); if (count === 43) return value;
  }

  return value;
}

/**
 * Deserializes multiple concatenated MemoryPack arrays directly to InternedNodeDto[].
 * Used for streamed responses where the server sends batches as separate arrays.
 * 
 * This is more memory-efficient than deserializeConcatenatedNodeArrays + internNodeArray
 * because strings are interned during deserialization, avoiding the intermediate NodeDto[].
 * 
 * Prerequisites:
 * - InternedNodeDto.setStorage() must be called before using this function
 * - If interning is disabled, strings will be stored directly (same behavior as internNode)
 * 
 * @param buffer - ArrayBuffer containing multiple concatenated MemoryPack arrays
 * @returns Array of InternedNodeDto, or null if empty
 */
export function deserializeConcatenatedToInternedNodeDtos(buffer: ArrayBuffer): InternedNodeDto[] | null {
  const reader = new MemoryPackReader(buffer);
  const allNodes: InternedNodeDto[] = [];

  // Read arrays until we've consumed the entire buffer
  while (!reader.isEnd()) {
    const batch = reader.readArray(r => deserializeNodeToInterned(r));
    if (batch) {
      for (const node of batch) {
        if (node !== null) {
          allNodes.push(node);
        }
      }
    }
  }

  return allNodes.length > 0 ? allNodes : null;
}

/**
 * Deserializes a single MemoryPack array directly to InternedNodeDto[].
 * 
 * @param buffer - ArrayBuffer containing a single MemoryPack array
 * @returns Array of non-null InternedNodeDto objects, or null if empty
 */
export function deserializeArrayToInternedNodeDtos(buffer: ArrayBuffer): InternedNodeDto[] | null {
  const reader = new MemoryPackReader(buffer);
  const rawNodes = reader.readArray(r => deserializeNodeToInterned(r));
  if (!rawNodes) return null;

  const nodes = rawNodes.filter((n): n is InternedNodeDto => n !== null);
  return nodes.length > 0 ? nodes : null;
}
